/**
 * Fee Type to TRID Section Mapping
 *
 * Maps Encompass V3 fee types to their corresponding TRID Loan Estimate sections.
 * Used by useLeFieldsMapper to populate individual fee line items within each section.
 */

/**
 * Maps Encompass fee types to TRID section letters (A-H)
 */
export const FEE_TYPE_TO_SECTION: Record<string, string> = {
  // Section A - Origination Charges
  LoanOriginationFee: 'A',
  OriginationFee: 'A',
  ProcessingFee: 'A',
  UnderwritingFee: 'A',
  MortgageBrokerFee: 'A',
  ApplicationFee: 'A',
  CommitmentFee: 'A',
  AdminFee: 'A',
  LoanDiscountFee: 'A',

  // Section B - Services You Cannot Shop For
  AppraisalFee: 'B',
  CreditReportFee: 'B',
  TaxServiceFee: 'B',
  FloodCertificationFee: 'B',
  FloodDeterminationFee: 'B',
  FloodInsurance: 'B',
  TaxStatusResearchFee: 'B',
  VerificationFee: 'B',

  // Section C - Services You Can Shop For
  TitleServices: 'C',
  TitleSearchFee: 'C',
  TitleExaminationFee: 'C',
  TitleInsuranceBinder: 'C',
  SettlementFee: 'C',
  ClosingFee: 'C',
  ClosingOrEscrowFee: 'C',
  EscrowFee: 'C',
  LendersTitleInsurance: 'C',
  LenderTitleInsurance: 'C',
  OwnersTitleInsurance: 'C',
  OwnerTitleInsurance: 'C',
  TitleInsurance: 'C',
  SurveyFee: 'C',
  PestInspectionFee: 'C',
  AttorneyFee: 'C',
  NotaryFee: 'C',

  // Section E - Taxes and Government Fees
  RecordingFees: 'E',
  RecordingFee: 'E',
  TransferTaxes: 'E',
  TransferTax: 'E',
  CityCountyTax: 'E',
  StateTax: 'E',
  StampTax: 'E',
  IntangibleTax: 'E',
  MortgageTax: 'E',

  // Section F - Prepaids
  PrepaidInterest: 'F',
  DailyInterest: 'F',
  HazardInsurancePremium: 'F',
  HomeownersInsurance: 'F',
  HomeownersInsurancePremium: 'F',
  MortgageInsurancePremium: 'F',
  MIPremium: 'F',
  PropertyTaxes: 'F',
  PropertyTax: 'F',
  PrepaidPropertyTax: 'F',

  // Section G - Initial Escrow Payment at Closing
  HazardInsuranceReserv: 'G',
  HazardInsuranceReserve: 'G',
  HomeownersInsuranceReserve: 'G',
  MortgageInsuranceReserv: 'G',
  MortgageInsuranceReserve: 'G',
  PropertyTaxReserv: 'G',
  PropertyTaxReserve: 'G',
  EscrowWaiver: 'G',
  AggregateAdjustment: 'G',

  // Section H - Other
  UserDefined: 'H',
  HOAFee: 'H',
  HOADues: 'H',
  HomeWarranty: 'H',
  CondoFee: 'H',
  InspectionFee: 'H',
  MiscFee: 'H',
  Other: 'H'
};

/**
 * Human-readable labels for fee types
 */
export const FEE_TYPE_LABELS: Record<string, string> = {
  // Section A
  LoanOriginationFee: 'Origination Fee',
  OriginationFee: 'Origination Fee',
  ProcessingFee: 'Processing Fee',
  UnderwritingFee: 'Underwriting Fee',
  MortgageBrokerFee: 'Broker Fee',
  ApplicationFee: 'Application Fee',
  CommitmentFee: 'Commitment Fee',
  AdminFee: 'Administrative Fee',
  LoanDiscountFee: 'Discount Points',

  // Section B
  AppraisalFee: 'Appraisal Fee',
  CreditReportFee: 'Credit Report',
  TaxServiceFee: 'Tax Service Fee',
  FloodCertificationFee: 'Flood Certification',
  FloodDeterminationFee: 'Flood Determination',
  FloodInsurance: 'Flood Insurance',
  TaxStatusResearchFee: 'Tax Status Research',
  VerificationFee: 'Verification Fee',

  // Section C
  TitleServices: 'Title Services',
  TitleSearchFee: 'Title Search',
  TitleExaminationFee: 'Title Examination',
  TitleInsuranceBinder: 'Title Insurance Binder',
  SettlementFee: 'Settlement/Closing Fee',
  ClosingFee: 'Closing Fee',
  ClosingOrEscrowFee: 'Closing/Escrow Fee',
  EscrowFee: 'Escrow Fee',
  LendersTitleInsurance: "Lender's Title Insurance",
  LenderTitleInsurance: "Lender's Title Insurance",
  OwnersTitleInsurance: "Owner's Title Insurance",
  OwnerTitleInsurance: "Owner's Title Insurance",
  TitleInsurance: 'Title Insurance',
  SurveyFee: 'Survey Fee',
  PestInspectionFee: 'Pest Inspection',
  AttorneyFee: 'Attorney Fee',
  NotaryFee: 'Notary Fee',

  // Section E
  RecordingFees: 'Recording Fees',
  RecordingFee: 'Recording Fee',
  TransferTaxes: 'Transfer Taxes',
  TransferTax: 'Transfer Tax',
  CityCountyTax: 'City/County Tax',
  StateTax: 'State Tax',
  StampTax: 'Stamp Tax',
  IntangibleTax: 'Intangible Tax',
  MortgageTax: 'Mortgage Tax',

  // Section F
  PrepaidInterest: 'Prepaid Interest',
  DailyInterest: 'Daily Interest',
  HazardInsurancePremium: "Homeowner's Insurance Premium",
  HomeownersInsurance: "Homeowner's Insurance",
  HomeownersInsurancePremium: "Homeowner's Insurance Premium",
  MortgageInsurancePremium: 'Mortgage Insurance Premium',
  MIPremium: 'Mortgage Insurance Premium',
  PropertyTaxes: 'Property Taxes',
  PropertyTax: 'Property Tax',
  PrepaidPropertyTax: 'Prepaid Property Tax',

  // Section G
  HazardInsuranceReserv: "Homeowner's Insurance Reserve",
  HazardInsuranceReserve: "Homeowner's Insurance Reserve",
  HomeownersInsuranceReserve: "Homeowner's Insurance Reserve",
  MortgageInsuranceReserv: 'Mortgage Insurance Reserve',
  MortgageInsuranceReserve: 'Mortgage Insurance Reserve',
  PropertyTaxReserv: 'Property Tax Reserve',
  PropertyTaxReserve: 'Property Tax Reserve',
  EscrowWaiver: 'Aggregate Escrow Adjustment',
  AggregateAdjustment: 'Aggregate Adjustment',

  // Section H
  UserDefined: 'Other Fee',
  HOAFee: 'HOA Fee',
  HOADues: 'HOA Dues',
  HomeWarranty: 'Home Warranty',
  CondoFee: 'Condo Fee',
  InspectionFee: 'Inspection Fee',
  MiscFee: 'Miscellaneous Fee',
  Other: 'Other'
};

/**
 * Section titles for display
 */
export const SECTION_TITLES: Record<string, string> = {
  A: 'A. Origination Charges',
  B: 'B. Services You Cannot Shop For',
  C: 'C. Services You Can Shop For',
  D: 'D. Total Loan Costs (A + B + C)',
  E: 'E. Taxes and Other Government Fees',
  F: 'F. Prepaids',
  G: 'G. Initial Escrow Payment at Closing',
  H: 'H. Other',
  I: 'I. Total Other Costs (E + F + G + H)',
  J: 'J. Total Closing Costs (D + I)'
};

/**
 * Get the section letter for a fee type
 */
export function getFeeSection(feeType: string): string {
  // Check for exact match
  if (FEE_TYPE_TO_SECTION[feeType]) {
    return FEE_TYPE_TO_SECTION[feeType];
  }

  // Check for UserDefined fees (e.g., UserDefined_1, UserDefined1003)
  if (feeType.startsWith('UserDefined')) {
    return 'H';
  }

  // Default to H (Other) for unknown fee types
  return 'H';
}

/**
 * Get the display label for a fee type
 */
export function getFeeLabel(feeType: string, description?: string): string {
  // Use description if provided and available
  if (description && description.trim()) {
    return description;
  }

  // Check for exact match
  if (FEE_TYPE_LABELS[feeType]) {
    return FEE_TYPE_LABELS[feeType];
  }

  // Convert camelCase/PascalCase to readable string
  return feeType
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
